import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { SEO, LanguageSwitcher } from '@/components/common';
import { useTranslation } from '@/features/i18n';

export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const { t } = useTranslation();

  const loginSchema = z.object({
    email: z.string().email(t.auth.login.error),
    password: z.string().min(6, t.auth.login.error),
  });

  type LoginFormData = z.infer<typeof loginSchema>;

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/admin/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    const { error } = await signIn(data.email, data.password);
    if (error) {
      setError(t.auth.login.error);
    } else {
      navigate(from, { replace: true });
    }
  };

  return (
    <>
      <SEO title={t.auth.login.title} noIndex />

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-800 px-4 py-8 sm:py-4 relative">
        {/* Language switcher - top right */}
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
          <LanguageSwitcher />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-6 sm:mb-8">
            <Link to="/" className="inline-block">
              <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
                Din AI Bedrift
              </h1>
            </Link>
            <p className="mt-2 text-sm sm:text-base text-neutral-600 dark:text-neutral-400">
              {t.auth.login.subtitle}
            </p>
          </div>

          <Card padding="lg" className="p-4 sm:p-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {error && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}

              <Input
                label={t.auth.login.email}
                type="email"
                placeholder="admin@dinaibedrift.no"
                error={errors.email?.message}
                {...register('email')}
              />

              <Input
                label={t.auth.login.password}
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                error={errors.password?.message}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="hover:text-neutral-600 dark:hover:text-neutral-300"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                }
                {...register('password')}
              />

              <Button
                type="submit"
                className="w-full"
                isLoading={isSubmitting}
                rightIcon={<LogIn className="w-4 h-4" />}
              >
                {t.auth.login.submit}
              </Button>
            </form>
          </Card>

          <p className="mt-6 text-center text-sm text-neutral-500 dark:text-neutral-500">
            <Link to="/" className="hover:text-primary-600 dark:hover:text-primary-400">
              {t.common.backTo} {t.nav.home.toLowerCase()}
            </Link>
          </p>
        </motion.div>
      </div>
    </>
  );
}
